import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import ProtectedImage from '../ProtectedImage';

const NewestMembers = () => {
  const { accessToken } = useAuthStore();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNewestMembers();
  }, [accessToken]);

  const fetchNewestMembers = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/profile/newest-members?limit=8', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setMembers(data.members || []);
        setError(null);
      } else {
        setError(data.message || 'Failed to load members');
      }
    } catch (err) {
      setError('Failed to load members');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMemberClick = (memberId) => {
    // Navigate to member's dashboard page
    navigate(`/profile/${memberId}/dashboard`);
  };

  const getRoleLabel = (roleCode) => {
    switch (roleCode) {
      case 'MASTER':
        return 'Master';
      case 'SLAVE':
        return 'slave';
      case 'SWITCH':
        return 'Switch';
      case 'OBSERVER':
        return 'Observer';
      default:
        return '';
    }
  };

  const getRoleBadgeColor = (roleCode) => {
    switch (roleCode) {
      case 'MASTER':
        return 'bg-red-600 dark:bg-red-500';
      case 'SLAVE':
        return 'bg-blue-600 dark:bg-blue-500';
      case 'SWITCH':
        return 'bg-purple-600 dark:bg-purple-500';
      case 'OBSERVER':
        return 'bg-gray-600 dark:bg-gray-500';
      default:
        return 'bg-gray-600';
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;

    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="surface rounded-lg p-6">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-primary mb-6">Newest Members</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {members.map((member) => (
          <div
            key={member.id}
            onClick={() => handleMemberClick(member.id)}
            className="surface border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-pointer hover:shadow-xl transition-all hover:scale-105"
          >
            {/* Avatar */}
            <div className="flex justify-center mb-3">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                {member.avatar_uuid ? (
                  <ProtectedImage
                    uuid={member.avatar_uuid}
                    alt={member.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl text-gray-400">
                    <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Display Name with Age */}
            <h3 className="text-lg font-semibold text-primary text-center mb-2">
              {member.display_name}
              {member.date_of_birth && ` (${calculateAge(member.date_of_birth)})`}
            </h3>

            {/* Role Badge */}
            <div className="flex justify-center">
              <span className={`px-3 py-1 text-xs font-medium text-white rounded-full ${getRoleBadgeColor(member.role_code)}`}>
                {getRoleLabel(member.role_code)}
              </span>
            </div>

            {/* Member Since */}
            <p className="text-xs text-secondary text-center mt-3">
              Member since {new Date(member.created_at).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>

      {members.length === 0 && (
        <p className="text-center text-secondary py-8">No members found</p>
      )}
    </div>
  );
};

export default NewestMembers;
